import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  clearAiToolPaymentFailureState,
  clearAiToolResultInFlight,
  clearCachedAiToolResult,
  clearPendingAiToolPayment,
  getAiToolPaymentErrorMessage,
  getCachedAiToolResult,
  getPendingAiToolPayment,
  isAiToolPaymentFailureStatus,
  runAiTool,
  saveCachedAiToolResult,
  tryMarkAiToolResultInFlight,
  waitForCachedAiToolResult,
} from '../utils/aiTool/payment'
import { buildAiToolResultState } from '../utils/aiTool/resultsMappers'
import { getAiToolFormPath } from '../utils/aiTool/resultsPaths'

export function useAiToolResultsPage(toolType, streamMessages) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const paymentId = searchParams.get('paymentId')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [streamMsg, setStreamMsg] = useState(streamMessages[0])
  const [resultState, setResultState] = useState(null)
  const msgTimerRef = useRef(null)

  const resetToTool = useCallback(() => {
    if (paymentId) {
      clearCachedAiToolResult(paymentId)
      clearAiToolResultInFlight(paymentId)
    }
    navigate(getAiToolFormPath(toolType), { replace: true })
  }, [navigate, paymentId, toolType])

  const redirectToForm = useCallback(() => {
    navigate(getAiToolFormPath(toolType), { replace: true })
  }, [navigate, toolType])

  const clearStreamTimer = useCallback(() => {
    if (msgTimerRef.current) {
      clearInterval(msgTimerRef.current)
      msgTimerRef.current = null
    }
  }, [])

  const startStreamTimer = useCallback(() => {
    clearStreamTimer()
    setStreamMsg(streamMessages[0])
    let msgIdx = 0
    msgTimerRef.current = setInterval(() => {
      msgIdx = Math.min(streamMessages.length - 1, msgIdx + 1)
      setStreamMsg(streamMessages[msgIdx])
    }, 2000)
  }, [clearStreamTimer, streamMessages])

  const applyCachedResult = useCallback((cachedResult) => {
    setResultState(cachedResult)
    setError('')
    setLoading(false)
  }, [])

  useEffect(() => {
    const status = searchParams.get('status')
    const paymentErrorParam = searchParams.get('paymentError')

    if (!isAiToolPaymentFailureStatus(status) && !paymentErrorParam) {
      return undefined
    }

    if (paymentId) {
      clearAiToolPaymentFailureState(paymentId)
    }

    const message = getAiToolPaymentErrorMessage(searchParams)
    navigate(
      `${getAiToolFormPath(toolType)}?paymentError=${encodeURIComponent(message)}${
        paymentId ? `&paymentId=${encodeURIComponent(paymentId)}` : ''
      }`,
      { replace: true },
    )

    return undefined
  }, [navigate, paymentId, searchParams, toolType])

  useEffect(() => {
    let cancelled = false

    const loadResults = async () => {
      const status = searchParams.get('status')
      const paymentErrorParam = searchParams.get('paymentError')

      if (isAiToolPaymentFailureStatus(status) || paymentErrorParam) {
        return
      }

      if (!paymentId) {
        redirectToForm()
        return
      }

      const cachedResult = getCachedAiToolResult(paymentId)
      if (cachedResult) {
        applyCachedResult(cachedResult)
        return
      }

      let pendingPayment = getPendingAiToolPayment(paymentId)

      if (!pendingPayment) {
        startStreamTimer()
        const waitedCache = await waitForCachedAiToolResult(paymentId)
        if (cancelled) return

        if (waitedCache) {
          clearStreamTimer()
          applyCachedResult(waitedCache)
          return
        }

        redirectToForm()
        return
      }

      if (pendingPayment.toolType !== toolType) {
        redirectToForm()
        return
      }

      if (!tryMarkAiToolResultInFlight(paymentId)) {
        startStreamTimer()
        const waitedCache = await waitForCachedAiToolResult(paymentId)
        if (cancelled) return

        clearStreamTimer()
        if (waitedCache) {
          applyCachedResult(waitedCache)
          return
        }

        pendingPayment = getPendingAiToolPayment(paymentId)
        if (!pendingPayment || pendingPayment.toolType !== toolType) {
          redirectToForm()
          return
        }

        if (!tryMarkAiToolResultInFlight(paymentId)) {
          const retryCache = await waitForCachedAiToolResult(paymentId, {
            maxWaitMs: 30000,
          })
          if (cancelled) return

          if (retryCache) {
            applyCachedResult(retryCache)
            return
          }

          redirectToForm()
          return
        }
      }

      startStreamTimer()

      try {
        const aiResult = await runAiTool({
          type: toolType,
          email: pendingPayment.email,
          fullName: pendingPayment.fullName,
          paymentId,
          formData: pendingPayment.formData,
        })

        if (cancelled) return

        const nextState = buildAiToolResultState(
          toolType,
          aiResult,
          pendingPayment,
        )
        saveCachedAiToolResult(paymentId, nextState)
        clearPendingAiToolPayment(paymentId)
        setResultState(nextState)
        setError('')
      } catch (err) {
        if (cancelled) return
        clearPendingAiToolPayment(paymentId)
        setError(
          err?.response?.data?.message ||
            err.message ||
            'Unable to load your results. Please try again.',
        )
      } finally {
        clearAiToolResultInFlight(paymentId)
        if (!cancelled) {
          clearStreamTimer()
          setLoading(false)
        }
      }
    }

    loadResults()

    return () => {
      cancelled = true
      clearStreamTimer()
    }
  }, [
    paymentId,
    toolType,
    searchParams,
    applyCachedResult,
    clearStreamTimer,
    redirectToForm,
    startStreamTimer,
  ])

  return { loading, error, streamMsg, resultState, paymentId, resetToTool }
}
